import { useState } from 'react';
import { AlertCircle, CheckCircle2 } from 'lucide-react';
import {
  type SecurityMode,
  useOrgSecurityPolicy,
  useSetOrgSecurityPolicy,
} from '@/hooks/useSecurityPolicy';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import SecurityModeSelector from './SecurityModeSelector';

/**
 * Org-wide default port security mode picker. Embedded inside
 * OrganizationSettings.
 */
export default function OrgSecurityPolicySection() {
  const { data, isLoading } = useOrgSecurityPolicy();
  const setMutation = useSetOrgSecurityPolicy();
  const [selected, setSelected] = useState<SecurityMode>('open');

  // Seed from the server value on load and whenever it changes (render-time, not an effect).
  const [synced, setSynced] = useState<typeof data>(undefined);
  if (data !== synced) {
    setSynced(data);
    if (data) setSelected(data.defaultPortSecurityMode);
  }

  if (isLoading) {
    return <Skeleton className="h-32 w-full" />;
  }

  return (
    <div>
      <h3 className="text-base font-semibold mb-1">
        Default port security mode (organization-wide)
      </h3>
      <p className="text-sm text-muted-foreground mb-3">
        Applied to all ports unless overridden at the region, device, or port level.
      </p>
      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <SecurityModeSelector
          value={selected}
          onChange={(v) => {
            if (v) setSelected(v);
          }}
          allowInherit={false}
        />
        <Button onClick={() => { setMutation.mutate(selected); }} disabled={setMutation.isPending}>
          {setMutation.isPending ? 'Saving…' : 'Save'}
        </Button>
      </div>
      {setMutation.isError ? (
        <p className="text-xs text-destructive mt-2 inline-flex items-center gap-1.5">
          <AlertCircle className="w-3.5 h-3.5" />
          Save failed.
        </p>
      ) : null}
      {setMutation.isSuccess ? (
        <p className="text-xs text-sev-4 mt-2 inline-flex items-center gap-1.5">
          <CheckCircle2 className="w-3.5 h-3.5" />
          Saved.
        </p>
      ) : null}
    </div>
  );
}
